'use client';

import { clsx } from 'clsx';
import { FC, MouseEventHandler, useState } from 'react';
import { IconButton } from 'atoms/IconButton';
import { Image } from 'atoms/Image/Image';
import { Link } from 'atoms/Link/Link';
import { ParseBBCode } from 'atoms/ParseBBCode';
import { Body, CTA, Title } from 'atoms/Typography/Typography';
import { DeliveryStatus, DeliveryStatusType } from 'molecules/DeliveryStatus';
import { Price } from 'molecules/Price';
import { ProductBullets } from 'molecules/ProductBullets';
import { ProductImage } from 'molecules/ProductImage';
import { ProductReferencePrice } from 'molecules/ProductReferencePrice';
import { ProductSize } from 'molecules/ProductSize';
import { ReviewsRating } from 'molecules/ReviewsRating';
import { SelectSizeButtons } from 'molecules/SelectSizeButtons';
import { Vat } from 'molecules/Vat';
import { WishlistIcon } from 'molecules/WishlistIcon';
import { ProductTileProps } from 'organisms/ProductTile/types';
import { ProductMaterialInfo } from 'pages/ProductDetail/ProductMaterialInfo';
import { UpsellingBannerModal } from 'pages/ProductDetail/UpsellingBannerModal';
import { useProductConfig } from 'pages/ProductDetail/hooks/useProductConfig';
import { gtmPushItemDetailClick } from 'modules/gtm/modules/sections/productDetails/productDetails';
import { parseDataToGTMItemViewDetail } from 'modules/gtm/utils/parsers';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useMediaQuery } from 'modules/mui/hooks';
import { useRouter } from 'modules/next/hooks';
import { useCart } from 'modules/shop/cart/hooks';
import navigationManager from 'modules/shop/navigationManager';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';
import {
  DAILY_DEAL_PRODUCT_BADGE_URL,
  DAILY_DEAL_PRODUCT_LABEL,
} from 'src/client/components/constants';
import { ProductDiscount } from '../ProductPriceSection/PriceInfo';
import styles from './ProductTile.module.css';

export const ProductTile: FC<ProductTileProps> = ({
  product,
  gtmCategoryName,
  displaySalePrices,
  upsellingBanner,
  classes,
}) => {
  // reduce amount of elements on listing page
  const { isMobile } = useMediaQuery();
  const t = useTranslations();
  const { gtmData } = useSiteConfig();
  const pageType = gtmData?.pageType;
  const { addToCart } = useCart();
  const [isSizeSelectVisible, setIsSizeSelectVisible] = useState(false);
  const { push } = useRouter();
  const [openBannerModal, setOpenBannerModal] = useState(false);
  const { hasSizes } = useProductConfig(product, product.referenceKey);

  if (!product) {
    return null;
  }

  const activeVariantPrice = product.price;

  const currentPrice = activeVariantPrice.withTax;
  const toggleSizeSelectVisibility = () =>
    setIsSizeSelectVisible(!isSizeSelectVisible);

  const setProductCategoryIdToLocalStorage = async () => {
    if (gtmCategoryName) {
      navigationManager.addStorageCategory(product, gtmCategoryName);
    }
  };

  const pushGtmItemDetailClick = () => {
    const detailViewData = parseDataToGTMItemViewDetail(product);

    gtmPushItemDetailClick({
      currency: detailViewData.currency,
      item: detailViewData.item,
    });
  };

  const handleProductClick = () => {
    setProductCategoryIdToLocalStorage(); // need to set correct product category for gtm
    pushGtmItemDetailClick();
  };

  const handleReviewClick: MouseEventHandler = (e) => {
    e.preventDefault();
    pushGtmItemDetailClick();
    push(`${product.href}#reviews`);
  };

  const toggleBannerModal = () => setOpenBannerModal((open) => !open);

  const isDailyDealProduct =
    product.isPrimaryPage && product.label === DAILY_DEAL_PRODUCT_LABEL;

  const isPossibleToAdd = product.seoUrl !== 'null' && !!product.seoUrl;

  return (
    <div
      className={clsx(
        styles.root,
        styles.tile,
        !isMobile && !isSizeSelectVisible && styles.rootHoverEffect,
        classes?.root,
      )}
    >
      {isDailyDealProduct && (
        <Image
          className={styles.badge}
          src={DAILY_DEAL_PRODUCT_BADGE_URL}
          alt={t('product.tile.tages-tipp')}
        />
      )}
      <Link
        href={product.href}
        className={styles.contentWrapper}
        onClick={handleProductClick}
      >
        <div className={clsx(styles.topContent, styles.container)}>
          {!isDailyDealProduct && product.label && (
            <CTA type="md" className={styles.label}>
              {product.label.toLowerCase()}
            </CTA>
          )}
          <WishlistIcon
            className={styles.wishlist}
            productName={product.name}
            variantId={product?.variants?.[0].id}
          />
        </div>
        <div className={clsx(styles.centerContent, styles.container)}>
          <ProductImage
            hash={product.coverImage}
            className={clsx(styles.cover, classes?.cover)}
          />

          <>
            <div className={styles.center}>
              <div className={styles.row}>
                <ReviewsRating
                  onClick={handleReviewClick}
                  url={product.href}
                  value={product.ratingScore}
                  count={product.ratingCount}
                  size={isMobile ? 'small' : 'medium'}
                />
              </div>
              <div className={styles.textContainer}>
                <Body type="md" className={styles.brand}>
                  {product.brand}
                </Body>
                <Title type="md" className={styles.name}>
                  <ParseBBCode content={product.name} />
                </Title>
              </div>
              {(activeVariantPrice?.reference ||
                product.composedMaterials?.value) && (
                <div className={styles.attributes}>
                  {activeVariantPrice?.reference && (
                    <ProductReferencePrice
                      price={activeVariantPrice}
                      className={styles.measurable}
                    />
                  )}
                  <ProductMaterialInfo
                    size={product.size}
                    composedMaterials={product.composedMaterials}
                    classes={{
                      root: styles.tooltip,
                      arrow: styles.tooltipArrow,
                    }}
                  />
                  {product.size && <ProductSize size={product.size} />}
                </div>
              )}
              {!isMobile && (
                <ProductBullets
                  bullets={product.bullets}
                  size="regular"
                  classes={{
                    list: styles.bulletList,
                  }}
                />
              )}
            </div>
          </>
        </div>
      </Link>
      {isSizeSelectVisible && (
        <SelectSizeButtons
          open={isSizeSelectVisible}
          classes={{ wrapper: styles.sizeSelectWrapper }}
          onClose={toggleSizeSelectVisibility}
          sizes={product.size}
          onClick={(size) => {
            isPossibleToAdd &&
              size.referenceKey &&
              addToCart(size.referenceKey, 1, pageType, product.limitForCart);
            upsellingBanner && toggleBannerModal();
            setProductCategoryIdToLocalStorage(); // need to set correct product category for gtm
            toggleSizeSelectVisibility();
          }}
        />
      )}

      <div className={clsx(styles.bottom, styles.container)}>
        <div className={styles.bottomContentPart}>
          <ProductDiscount
            discountPercentage={product.discountPercentage}
            recommendedPrice={product.price.recommendedRetailPrice}
            currencyCode={product.price.currencyCode}
          />

          <Price
            amount={currentPrice}
            currencyCode={product.price.currencyCode}
            className={clsx(displaySalePrices && styles.salePrice)}
            isCrossed={false}
            size="regular"
          />
          {!isMobile && (
            <Vat
              rate={product.taxRatePercent}
              referenceKey={product.referenceKey}
              size="small"
              className={styles.bottomVat}
            />
          )}
        </div>

        {!isMobile && (
          <IconButton
            iconSvgType="cart"
            className={styles.addToCartButton}
            onClick={() => {
              if (hasSizes) {
                toggleSizeSelectVisibility();
              } else if (product.referenceKey) {
                toggleBannerModal();
                isPossibleToAdd &&
                  addToCart(
                    product.referenceKey,
                    1,
                    pageType,
                    product.limitForCart,
                  );
                setProductCategoryIdToLocalStorage(); // need to set correct product category for gtm
              }
            }}
          />
        )}

        {!isMobile && (
          <div className={styles.deliveryStatus}>
            <DeliveryStatus
              status={DeliveryStatusType.AVAILABLE}
              textClassName={styles.deliveryText}
            />
          </div>
        )}
      </div>

      {upsellingBanner && (
        <UpsellingBannerModal
          open={openBannerModal}
          onClose={toggleBannerModal}
          banner={upsellingBanner}
        />
      )}
    </div>
  );
};
